/**
 * FilterBar Types
 *
 * Shared type definitions for the FilterBar component system.
 * Supports various filter types: search, select, multi-select, toggle, date-range.
 */

import { ReactNode } from 'react';

// ============================================================================
// Filter Definition Types
// ============================================================================

export type FilterType = 'search' | 'select' | 'multi-select' | 'toggle' | 'date-range';

export interface FilterOption {
  value: string;
  label: string;
  icon?: ReactNode;
  color?: string;
  disabled?: boolean;
}

export interface FilterDefinition {
  /** Unique identifier for the filter */
  key: string;
  /** Display label */
  label: string;
  /** Filter input type */
  type: FilterType;
  /** Placeholder text */
  placeholder?: string;
  /** Options for select/multi-select filters */
  options?: FilterOption[];
  /** Width variant */
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Whether to show on mobile (default: true for first 2 filters) */
  showOnMobile?: boolean;
  /** Group name for "More Filters" dropdown */
  group?: string;
  /** Custom icon to show before input */
  icon?: ReactNode;
  /** Debounce delay in ms (for search inputs) */
  debounceMs?: number;
  /** Whether this filter is disabled */
  disabled?: boolean;
}

export interface FilterConfig {
  /** Filter definitions */
  filters: FilterDefinition[];
  /** Show "Clear All" button when filters are active */
  showClearAll?: boolean;
  /** Show active filter chips below the filter bar */
  showActiveChips?: boolean;
  /** Layout variant */
  layout?: 'inline' | 'stacked' | 'responsive';
  /** Number of filters to show before "More Filters" dropdown */
  moreFiltersThreshold?: number;
  /** Search input placeholder (shorthand for search filter) */
  searchPlaceholder?: string;
}

// ============================================================================
// Active Filter (for chips display)
// ============================================================================

export interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
  value: unknown;
}

// ============================================================================
// FilterBar Props
// ============================================================================

export interface FilterBarProps {
  /** Filter configuration */
  config: FilterConfig;
  /** Current filter values */
  values: Record<string, unknown>;
  /** Called when a filter value changes */
  onChange: (key: string, value: unknown) => void;
  /** Called when a filter is cleared */
  onClear?: (key: string) => void;
  /** Called when all filters are cleared */
  onClearAll?: () => void;
  /** Active filters for chip display */
  activeFilters?: ActiveFilter[];
  /** Loading state */
  loading?: boolean;
  /** Additional className */
  className?: string;
}

// ============================================================================
// Sub-component Props
// ============================================================================

export interface FilterInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: ReactNode;
  debounceMs?: number;
  disabled?: boolean;
  className?: string;
}

export interface FilterSelectProps {
  value: string | undefined;
  onChange: (value: string) => void;
  options: FilterOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export interface FilterMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: FilterOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export interface FilterToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
  className?: string;
}

export interface FilterChipsProps {
  activeFilters: ActiveFilter[];
  onRemove: (key: string) => void;
  onClearAll?: () => void;
  className?: string;
}

// ============================================================================
// Width Classes
// ============================================================================

export const FILTER_WIDTH_CLASSES: Record<NonNullable<FilterDefinition['width']>, string> = {
  sm: 'w-32',
  md: 'w-40',
  lg: 'w-56',
  xl: 'w-72',
  full: 'w-full',
};
